// GoalDetail.js

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import 'bootstrap-icons/font/bootstrap-icons.css';
import GoalInfo from './GoalInfo';
import TargetsList from './TargetsList.js';
import IndicatorDetails from './IndicatorDetails';
import GoalDetailMobile from './GoalDetailMobile';
import SDGSpinner from './SDGSpinner';
import { GoalsContext } from '../State/GoalsContext';
import { TargetsContext } from '../State/TargetsContext';
import { normalizeGoalType } from '../utils/goalTypeUtils';
import '../utils/sharedStyles.css';

const GoalDetail = () => {

  const { selectedGoal, fetchGoal, loading: goalLoading, goalType: contextGoalType } = useContext(GoalsContext);
  const { targets, loading: targetsLoading, fetchTargetsByGoal } = useContext(TargetsContext);

  const { goalType: urlGoalType, goalId, targetId } = useParams();
  const goalType = normalizeGoalType(urlGoalType || contextGoalType);
  const [selectedTargetId, setSelectedTargetId] = useState(targetId || null);
  const [selectedTarget, setSelectedTarget] = useState(targets.find(t => t.id === parseInt(targetId)) || null);
  const [leftColumnWidth, setLeftColumnWidth] = useState(25); // Default 25%
  const [isResizing, setIsResizing] = useState(false);
  const resizeHandleRef = useRef(null);
  const containerRef = useRef(null);

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  // Sync selectedTargetId with URL parameter - clear when targetId is removed
  useEffect(() => {
    if (targetId) {
      const parsedId = parseInt(targetId);
      setSelectedTargetId(parsedId);
      if (targets.length > 0) {
        const match = targets.find(t => t.id === parsedId);
        if (match) {
          setSelectedTarget(match);
        } else {
          setSelectedTarget(null);
        }
      } else {
        setSelectedTarget(null);
      }
    } else {
      setSelectedTargetId(null);
      setSelectedTarget(null);
    }
  }, [targetId, targets]);

  // Fetch goal (goals are shared between EU/UN, no type needed)
  useEffect(() => {
    const parsedGoalId = parseInt(goalId);
    
    if (goalId && (!selectedGoal || selectedGoal.id !== parsedGoalId)) {
      fetchGoal(parsedGoalId);
    }
  }, [goalId, fetchGoal, selectedGoal]);

  // Track last fetched to prevent duplicate fetches
  const lastFetchedTargetsRef = useRef(null);
  
  // Fetch targets when goal is loaded
  useEffect(() => {
    // Don't run if selectedGoal is null (happens when fetchGoal clears it)
    if (!selectedGoal || !selectedGoal.id) {
      return;
    }
    
    const currentGoalType = goalType;
    const cacheKey = `${selectedGoal.id}-${currentGoalType}`;
    
    // Don't fetch if we've already fetched for this combination
    if (lastFetchedTargetsRef.current === cacheKey) {
      return;
    }
    
    lastFetchedTargetsRef.current = cacheKey;
    fetchTargetsByGoal(selectedGoal.id, currentGoalType);
    // Removed fetchTargetsByGoal from dependencies to prevent re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGoal?.id, goalType]);

  // Resize handlers - optimized for smooth, immediate updates
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current || !resizeHandleRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      const newWidthPercent = (mouseX / containerWidth) * 100;

      // Clamp between 15% and 35%
      const clampedWidth = Math.max(15, Math.min(35, newWidthPercent));
      
      // Log current width percentage
      console.log(`Left column width: ${clampedWidth.toFixed(2)}%`);
      
      // Update handle position directly via DOM for instant response
      if (resizeHandleRef.current) {
        resizeHandleRef.current.style.left = `${clampedWidth}%`;
      }
      
      // Also update state for column width (this will cause a re-render but handle is already positioned)
      setLeftColumnWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleTargetSelect = (targetId) => {
    setSelectedTargetId(targetId);
    const target = targets.find(t => t.id === targetId);
    setSelectedTarget(target || null);
  };
  
  // Show loading spinner while fetching goal or targets
  if (goalLoading || targetsLoading || !selectedGoal) {
    return <SDGSpinner size="lg" text="Loading goal details..." />;
  }

  if (!selectedGoal) return <p>Goal not found</p>;

  // Use mobile component on mobile devices
  if (isMobile) {
    return <GoalDetailMobile />;
  }

  return (
    <div className='container-full'>
      <Container 
        fluid 
        ref={containerRef}
        className={`goal-details-container ${isResizing ? 'resizing' : ''}`}
      >
        <Row className="row-full-height">
          <Col 
            xs={12} 
            md={4} 
            className="targets-column"
            style={{ 
              display: 'flex', 
              flexDirection: 'column',
              flex: `0 0 ${leftColumnWidth}%`,
              minWidth: '15%',
              maxWidth: '35%'
            }}
          >
            <div className="goal-info-content">
              <GoalInfo goal={selectedGoal} />
            </div>
            <div className="targets-list">
              <div className='section-title section-title-spacing'>
                <h6 className="sdg-section-title">Targets</h6>
              </div>
              <TargetsList
                targets={targets}
                goalId={goalId}
                activeTargetId={selectedTargetId}
                onTargetSelect={handleTargetSelect}
              />
            </div>
          </Col>
          
          {/* Resize Handle */}
          <div
            ref={resizeHandleRef}
            className={`resize-handle ${isResizing ? 'resizing' : ''}`}
            onMouseDown={handleMouseDown}
            title="Drag to resize columns"
            style={{
              position: 'absolute',
              left: `${leftColumnWidth}%`,
              transform: 'translateX(-50%)',
              zIndex: 1000
            }}
          />
          
          <Col 
            xs={12} 
            md={8} 
            className='indicators-column'
            style={{ 
              flex: '1',
              marginLeft: 0
            }}
          >
            <IndicatorDetails
              selectedTarget={selectedTarget}
              selectedTargetId={selectedTargetId}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GoalDetail;
